import React, { useCallback, useEffect, useMemo, useState } from 'react';
import { P2Button, P2ButtonGroup, P2TextDescription } from 'react-bootstrap-common-script';
import ApplicationCooLayout from '../../../layout/application-coo-layout';
import { TextSubheader } from '../../../components/text/text-subheader';
import { Stepper, StepperItem } from '../../../components/stepper';
import { SectionCard } from '../../../components/card';
import { Separator } from '../../../components/separator';
import CurrentProposedOwnerFinancialPlanSection from '../../../components/financial-plan/current-owners-financial-plan-section';
import PaymentRequiredSection from '../../../components/financial-plan/payment-required-section';
import { FinancialPlanContenxtInterface, useFinancialPlanContext } from '../../../context/financial-plan-contenxt';
import AdjustFinancialPlanModal from '../../../components/financial-plan/adjust-financial-plan-modal/adjust-financial-plan-modal';
import { DownloadSvg } from '../../../assets/svg';
import { useNavigate } from 'react-router-dom';
import useStageDStepper from '../../../hooks/use-stage-d-stepper';
import { useAppSelector } from '../../../redux/hook';
import { PolymerAxios } from '../../../utils/axios';
import { API_ApplicationStageUpdate, API_GetCurrentFinancialPlan } from '../../../utils/api';
import { StageDCurrentFinancialPlan } from '../../../models/classes/stage-d/Review-Application.class';
import { useGetStageD1ObjQuery } from '../../../redux/services/getStageD';
import { StructureStatusEnum } from '../../../models/enum/structure-status.enum';
import LottieLoader from '../../../components/loader/lottie-loader/lottie-loader';
import { downloadFile } from '../../../utils/download-file';
import { ApplicationStatusEnum } from '../../../models/enum/application-status.enum';
let activeStep = 3;

const StageDCurrentOwnerFinancialPlanPage = () => {
  const navigate = useNavigate();
  const { isAdjustFPVisible, toggleAdjustFPVisibility }: FinancialPlanContenxtInterface = useFinancialPlanContext();
  const [isPreliminary, setIsPreliminary] = useState<boolean>(true);
  const [infoboxStatus, setInfoboxStatus] = useState<'info' | 'success'>('info');
  const [infoboxTitle, setInfoboxTitle] = useState<string>('');
  const [infoboxText, setInfoboxText] = useState<string>('');
  const [currentFinancialPlanData, setCurrentFinancialPlanData] = useState<StageDCurrentFinancialPlan>();
  const [download_key, setDownloadKey] = useState<string>('');

  const loginUser = useAppSelector((state) => state.auth.AuthenticationSlice.loginUser);
  const application_uuid = useAppSelector((state) => state.app.ApplicationIdSlice.applicationId);
  const { stepperItems } = useStageDStepper(loginUser.uinfin, application_uuid || '');
  const updatedStepperItems = stepperItems.map((item) =>
    item.id === 1 ? { ...item, title: 'Application is Reviewed' } : item.id === 2 ? { ...item, title: 'In-Principle Approval is Granted' } : item
  );
  const startApplicationFormData = useAppSelector((state) => state.app.ApplicationStartSlice.formData);
  const singpassToken = useAppSelector((state) => state.auth.AuthenticationSlice.singpassToken);
  const applicationStep1FormData = useAppSelector((state) => state.app.ApplicationStep1Slice.formData);

  const loginUserStructureStatus = useMemo(() => {
    const structure_status =
      applicationStep1FormData?.household.incoming_owners.find((e) => e.myinfo.uinfin === loginUser.uinfin)?.structure_status ??
      StructureStatusEnum.outgoing;
    return structure_status;
  }, [applicationStep1FormData?.household, loginUser.uinfin]);

  const { data, isLoading } = useGetStageD1ObjQuery(
    {
      application_uuid: application_uuid ?? '',
      uinfin: loginUser?.uinfin ?? '',
      stage: 'D4',
      structure: loginUserStructureStatus,
      singpass_token: singpassToken ?? '',
    },
    { skip: !application_uuid || !loginUserStructureStatus }
  );

  const fetchFinancialPlan = async () => {
    try {
      const response = await PolymerAxios(loginUser?.uinfin ?? '', singpassToken).get(
        `${API_GetCurrentFinancialPlan}?application_uuid=${application_uuid}`
      );
      const data: StageDCurrentFinancialPlan = response.data;
      setCurrentFinancialPlanData(data);
      setIsPreliminary(data.scheduled_completion_date === undefined);
    } catch (error) {
      console.error('Error fetching financial plan:', error);
    }
  };

  useEffect(() => {
    if (currentFinancialPlanData === undefined) {
      fetchFinancialPlan();
    }
  }, []);

  useEffect(() => {
    if (isPreliminary) {
      setInfoboxStatus('info');
      setInfoboxTitle('Completion appointment date is still tentative');
      setInfoboxText(
        'The preliminary financial plan is based on your tentative completion appointment date. We will contact you to confirm the appointment date and time. If there is a change in your appointment date, a revised financial plan will be issued. '
      );
    } else {
      setInfoboxStatus('success');
      setInfoboxTitle('Completion appointment date is confirmed');
      setInfoboxText(
        'Your completion date is confirmed and the financial details are ready. Please review the financial plan before proceeding with completion.'
      );
    }
  }, [isPreliminary]);

  const isNextButtonEnabled =
    stepperItems.length > 3 ||
    (!isPreliminary &&
      (startApplicationFormData?.polymer_status === 'PREPARE_FOR_COMPLETION' ||
        startApplicationFormData?.polymer_status === 'PREPARE_FOR_COMPLETION_COMPLETE'));

  const handleNext = useCallback(async () => {
    if (stepperItems.length > 3) {
      // Direct navigation branch
      navigate(`/application-coo/stage-d/review-financial-plan-proposed`);
    } else {
      // API call to update polymer_stage + navigation branch
      const submittingData = {
        application_uuid,
        stage: 'E',
      };
      try {
        const response = await PolymerAxios(loginUser?.uinfin ?? '', singpassToken).post(API_ApplicationStageUpdate, submittingData);
        if (response.status === 200) {
          navigate(`/application-coo?application_id=${application_uuid}`);
        }
      } catch (error) {
        console.error('Error updating polymer status', error);
      }
    }
  }, [stepperItems.length, application_uuid, navigate, singpassToken]);

  const backNavigate = useCallback(() => {
    navigate(`/application-coo/stage-d/in-principle-approval?application_id=${application_uuid}`);
  }, [application_uuid]);

  const currentFinancialPlanPdf = useMemo(() => {
    const currentOwnerFinancialPlans = data?.folders?.filter((f) => f.name === 'Financial Plan for Current Owners') ?? [];
    if (currentOwnerFinancialPlans.length === 0) return undefined;

    // 2) Sort them by submitted_date, newest first
    currentOwnerFinancialPlans.sort((a, b) => {
      const dateA = new Date(a.comments[0]?.documents[0]?.submitted_date ?? '').getTime();
      const dateB = new Date(b.comments[0]?.documents[0]?.submitted_date ?? '').getTime();
      return dateB - dateA; // bigger (newer) dates bubble to index 0
    });

    return currentOwnerFinancialPlans[0];
  }, [data]);

  const handleDownload = useCallback(
    (key: string) => () => {
      console.log('downloading:', key);
      downloadFile(key, singpassToken);
    },
    [singpassToken]
  );

  useEffect(() => {
    if (currentFinancialPlanPdf !== undefined) {
      try {
        if (currentFinancialPlanPdf.comments.length > 0) {
          setDownloadKey(currentFinancialPlanPdf.comments[0].documents[0].key);
        }
      } catch (e) {
        console.log('download-button process download_key', e);
      }
    }
  }, [currentFinancialPlanPdf]);

  // DEBUG LOGGING
  console.groupCollapsed('[DEBUG] Next button display conditions');
  console.log({
    stepperItemsLength: stepperItems.length,
    isPreliminary,
    polymerStatus: startApplicationFormData?.polymer_status,
    isNextButtonEnabled,
  });
  console.groupEnd();

  return (
    <ApplicationCooLayout>
      {isLoading && <LottieLoader />}
      {!isLoading && (
        <div className='view-application-stage-d-container'>
          <TextSubheader subheaderMain='Stage D: Process Application' subheaderSub={startApplicationFormData.selected_hdb_property?.address} />
          <div className='stage-d-stepper-wrapper'>
            <Stepper activeStep={activeStep} items={updatedStepperItems} style={{ margin: '62px auto' }} />
          </div>

          <div className='d-flex justify-content-center'>
            {!isAdjustFPVisible && (
              <SectionCard
                required={false}
                className='flex-fill'
                titleChildren={
                  <>
                    <StepperItem variant='section_active' size={40} step={activeStep.toString()} marginBottom={'0px'} />
                    <P2TextDescription title={"Current Owners' Financial Plan"} />
                  </>
                }
                contentChildren={
                  <div className='polymer-stage-d-financial-plan-container'>
                    <div className='px-4'>
                      <CurrentProposedOwnerFinancialPlanSection
                        ownerType='current'
                        isPreliminary={isPreliminary}
                        infoboxStatus={infoboxStatus}
                        infoboxTitle={infoboxTitle}
                        infoboxText={infoboxText}
                        currentFinancialPlanData={currentFinancialPlanData!}
                      />
                    </div>
                    <Separator variant='solid' style={{ margin: '48px 0 24px' }} />
                    <div className='px-4'>
                      <PaymentRequiredSection currentFinancialPlanData={currentFinancialPlanData!} />
                    </div>
                  </div>
                }
              />
            )}
            {isAdjustFPVisible && <AdjustFinancialPlanModal activeStep={activeStep.toString()} toggleAdjustFPVisibility={toggleAdjustFPVisibility} />}
          </div>

          {!isAdjustFPVisible && (
            <React.Fragment>
              <Separator style={{ margin: '32px 0' }} />
              <div className='polymer-application-coo__card-button'>
                <P2ButtonGroup>
                  <P2Button variant={'outline-primary'} onClick={backNavigate}>
                    Back
                  </P2Button>
                  {download_key !== '' && (
                    <P2Button variant={'outline-primary'} onClick={handleDownload(download_key)} endIcon={<DownloadSvg strokeColor='#0C8188' />}>
                      Download financial plan
                    </P2Button>
                  )}
                  {isNextButtonEnabled && (
                    <P2Button onClick={handleNext} variant='primary-1'>
                      Next
                    </P2Button>
                  )}
                </P2ButtonGroup>
              </div>
            </React.Fragment>
          )}

          {isAdjustFPVisible && (
            <div className='polymer-adjust-financial-plan-modal__button-group-wrapper'>
              <Separator style={{ margin: '32px 0' }} />
              <div className='polymer-application-coo__card-button'>
                <P2ButtonGroup>
                  <P2Button variant={'outline-primary'} onClick={toggleAdjustFPVisibility}>
                    Cancel
                  </P2Button>
                </P2ButtonGroup>
              </div>
            </div>
          )}
        </div>
      )}
    </ApplicationCooLayout>
  );
};

export default StageDCurrentOwnerFinancialPlanPage;
